import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import type { ChartAccount, LocaleCode } from '@/api/models';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    authorClause,
    versionClauses,
    VERSION_STATUS_CHIP,
    type TimelineEntry,
} from '../../historyMeta';
import { VersionDiff } from '../versionDiff';

type VersionTimelineProps = {
    entries: TimelineEntry[];
    accounts: ChartAccount[];
    locale: LocaleCode;
    expanded: number[];
    onToggle: (version: number) => void;
    /** Open the Activity tab filtered to this operation. */
    onOpenActivity: () => void;
    isLoading: boolean;
};

const SKELETON_CARDS = 3;

/**
 * The version timeline: one card per version, newest first, on a rail.
 *
 * **Every version ever created is here, the draft included.** A timeline that
 * hid one would be answering a different question from the one it is on screen
 * to answer — the whole point is that any historical entry stays explicable by
 * the rules that were in force when it posted.
 *
 * **The author is on the card, opposite its number.** Not in the change log
 * alone: the log records acts and can start empty, while a version always knows
 * who created it, and "who decided this" is half of what a reader came here to
 * find out.
 *
 * **The newest card opens expanded and the rest start collapsed.** Eight
 * expanded diffs is a wall, and the change somebody came to look at is almost
 * always the last one.
 *
 * **"N entries posted under this version" links into the Activity tab**, filtered
 * to this operation. It is the bridge between "these were the rules" and "here
 * is what posted under them", and it is the only place the two halves of this
 * module meet. A version nothing has posted under says so and offers no link —
 * a link to an empty register is the dead link this affordance exists to avoid.
 */
export const VersionTimeline: React.FC<VersionTimelineProps> = ({
    entries,
    accounts,
    locale,
    expanded,
    onToggle,
    onOpenActivity,
    isLoading,
}) => {
    const { t } = useTranslation('pages');

    if (isLoading) {
        return (
            <ul
                className="flex flex-col gap-3"
                {...getNodeTestIdProp('operations-history-skeleton')}
            >
                {Array.from({ length: SKELETON_CARDS }, (_, index) => (
                    <li
                        className="flex flex-col gap-2 rounded-2xl border border-separator bg-surface p-4"
                        key={index}
                    >
                        <div className="h-4 w-40 animate-pulse rounded bg-surface-secondary" />
                        <div className="h-3 w-56 animate-pulse rounded bg-surface-secondary" />
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <ul
            className="flex flex-col gap-3 border-s border-separator ps-5"
            {...getNodeTestIdProp('operations-history-timeline')}
        >
            {entries.map((entry) => {
                const { version } = entry;
                const chip = VERSION_STATUS_CHIP[version.status];
                const isOpen = expanded.includes(version.version);
                const author = authorClause(version);

                return (
                    <li
                        className="relative flex flex-col gap-3 rounded-2xl border border-separator bg-surface p-4"
                        key={version.id}
                        {...getNodeTestIdProp(
                            `operations-history-version-${version.version}`,
                        )}
                    >
                        {/* The rail's dot, pulled out over the border the list
                            draws — the only colour on the rail, and it carries
                            the same distinction as the chip beside it. */}
                        <span
                            aria-hidden
                            className={cn(
                                'absolute -start-[27px] top-6 size-2.5 rounded-full ring-4 ring-surface',
                                chip.dotClassName,
                            )}
                        />

                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <span className="flex flex-wrap items-center gap-2">
                                <span className="text-base font-semibold text-foreground">
                                    {t('operations.history.versionLabel', {
                                        number: version.version,
                                    })}
                                </span>
                                <span
                                    className={cn(
                                        'inline-flex w-fit items-center rounded-lg px-2.5 py-1 text-xs font-medium',
                                        chip.className,
                                    )}
                                    {...getNodeTestIdProp(
                                        `operations-history-status-${version.version}`,
                                    )}
                                >
                                    {t(chip.labelKey)}
                                </span>
                            </span>
                            <span
                                className="text-xs text-muted"
                                {...getNodeTestIdProp(
                                    `operations-history-author-${version.version}`,
                                )}
                            >
                                {t(author.key, author.params)}
                            </span>
                        </div>

                        <span
                            className="text-sm tabular-nums text-muted"
                            {...getNodeTestIdProp(
                                `operations-history-meta-${version.version}`,
                            )}
                        >
                            {versionClauses(entry)
                                .map((clause) => t(clause.key, clause.params))
                                .join(' · ')}
                        </span>

                        <span
                            className="flex flex-wrap items-center gap-2 text-sm"
                            {...getNodeTestIdProp(
                                `operations-history-usage-${version.version}`,
                            )}
                        >
                            <span className="text-muted">
                                {t('operations.history.postedCount', {
                                    count: version.postedEntryCount,
                                })}
                            </span>
                            {version.postedEntryCount > 0 ? (
                                <Button
                                    onPress={onOpenActivity}
                                    size="sm"
                                    variant="ghost"
                                    {...getNodeTestIdProp(
                                        `operations-history-usage-link-${version.version}`,
                                    )}
                                >
                                    {t('operations.history.viewActivity')}
                                </Button>
                            ) : null}
                        </span>

                        {isOpen ? (
                            <VersionDiff
                                accounts={accounts}
                                diff={entry.diff}
                                locale={locale}
                                version={version.version}
                            />
                        ) : null}

                        <Button
                            className="w-fit"
                            onPress={() => onToggle(version.version)}
                            size="sm"
                            variant="ghost"
                            {...getNodeTestIdProp(
                                `operations-history-toggle-${version.version}`,
                            )}
                        >
                            <Icon
                                aria-hidden
                                icon={
                                    isOpen
                                        ? 'gravity-ui:chevron-up'
                                        : 'gravity-ui:chevron-down'
                                }
                            />
                            {t(
                                isOpen
                                    ? 'operations.history.hideChanges'
                                    : 'operations.history.showChanges',
                            )}
                        </Button>
                    </li>
                );
            })}
        </ul>
    );
};
